import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Careers = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Careers — BrightWings</title>
        <meta name="description" content="BrightWings pairs experienced engineers with learners inside real client work. Not a program. A system. See if there's a fit." />
        <link rel="canonical" href="https://brightwings.io/careers" />
        <meta property="og:url" content="https://brightwings.io/careers" />
        <meta property="og:title" content="Careers — BrightWings" />
        <meta property="og:description" content="BrightWings pairs experienced engineers with learners inside real client work. Not a program. A system. See if there's a fit." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Careers — BrightWings" />
        <meta name="twitter:description" content="BrightWings pairs experienced engineers with learners inside real client work. Not a program. A system. See if there's a fit." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Careers at BrightWings</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Build together. <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>Grow together.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '520px', margin: '0 auto' }}>
              BrightWings is a software company built on a simple belief: people learn best when they build together.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '16px' }}>
            We hire experienced software engineers.
          </p>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.8 }}>
            And when the conditions are right, we intentionally bring in non-traditional learners and pair them with those engineers.
          </p>
          <p style={{ fontSize: '20px', lineHeight: 1.6, marginTop: '28px', fontWeight: 600, color: 'var(--bw-dark)' }}>
            Not as a program.<br />As a system.
          </p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="bw-section-white">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '24px' }}>Who This Is For</h2>
          <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '28px' }}>
            BrightWings is for people who care about how work gets done.
          </p>
          <div className="space-y-5 mb-8">
            {[
              'Software engineers who want to stay sharp by teaching and leading',
              'Non-traditional learners looking for a real on-ramp into tech',
              'People who value clarity, context, and long-term growth',
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-4">
                <CheckCircle size={20} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>{point}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
            If you're looking for a bootcamp, a course, or a fast title jump, this isn't it.
          </p>
        </div>
      </section>

      {/* Non-Traditional Learners */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '24px' }}>Non-Traditional Learners</h2>
          <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '20px' }}>
            A non-traditional learner isn't defined by credentials. It's someone who:
          </p>
          <div className="space-y-4 mb-8">
            {[
              'Took a non-linear path into tech',
              'Learned through work, self-study, or career pivots',
              'Has curiosity and grit, but lacks proximity to experienced engineers',
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-4">
                <CheckCircle size={20} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>{point}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '16px' }}>
            When we bring learners on, they learn inside real client work. They're paired with software engineers who explain decisions as they're made.
          </p>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.8 }}>
            We don't always hire non-traditional learners. That's intentional. If we can't support them well, we don't bring them in.{' '}
            <span style={{ color: 'var(--bw-dark)', fontWeight: 600 }}>Everybody matters.</span>
          </p>
        </div>
      </section>

      {/* Software Engineers */}
      <section className="bw-section-white">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '24px' }}>Software Engineers</h2>
          <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '20px' }}>
            At BrightWings, mentorship is part of the job. We hire software engineers who:
          </p>
          <div className="space-y-4 mb-8">
            {[
              'Care about clarity, not just output',
              'Want to grow by teaching others',
              'Understand that leadership isn\'t a title, it\'s a practice',
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-4">
                <CheckCircle size={20} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>{point}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.8, marginBottom: '16px' }}>
            Teaching keeps engineers sharp. Explaining systems surfaces blind spots. Pairing prevents stagnation.
          </p>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.8 }}>
            If you don't want to mentor, this isn't the right role — and that's okay.
          </p>
        </div>
      </section>

      {/* How Learning Works */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '24px' }}>How Learning Works Here</h2>
          <div className="space-y-4 mb-8">
            {[
              'Real work, real systems, real constraints',
              'Side-by-side pairing, not lectures',
              'Progressive contribution over time',
              'Clear expectations on both sides',
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-4">
                <CheckCircle size={20} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>{point}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.8 }}>
            We aim to have 2–3 experienced engineers in place before engaging non-traditional learners. We build the foundation first.
          </p>
        </div>
      </section>

      {/* Everybody Matters */}
      <section className="bw-section-dark">
        <div className="max-w-4xl mx-auto text-center">
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, color: 'white', fontSize: 'clamp(28px, 3.5vw, 42px)', letterSpacing: '-0.025em', marginBottom: '28px' }}>
            Everybody Matters
          </h2>
          <div className="space-y-3 text-xl" style={{ color: 'rgba(255,255,255,0.75)' }}>
            <p>Engineers matter.</p>
            <p>Learners matter.</p>
            <p>The work matters.</p>
          </div>
          <p style={{ fontSize: '16px', marginTop: '28px', color: 'rgba(255,255,255,0.55)' }}>
            We're building something durable, not rushed.
          </p>
        </div>
      </section>

      {/* Stakes */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div style={{ borderLeft: '3px solid var(--bw-border)', paddingLeft: '20px' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>Most roles</p>
              <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
                Most engineers spend years in roles that don't stretch them. Most learners can't find a real on-ramp that respects what they already know.
              </p>
            </div>
            <div style={{ borderLeft: '3px solid var(--bw-blue)', paddingLeft: '20px' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>At BrightWings</p>
              <p style={{ fontSize: '16px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
                You work on real systems, with real constraints, alongside people who explain decisions as they make them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '40px' }}>Apply</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
              Apply as a Software Engineer
            </Link>
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              className="bw-btn-secondary"
              style={{ flexDirection: 'column', alignItems: 'center', gap: '2px' }}
            >
              <span>Apply as a Non-Traditional Learner</span>
              <span style={{ fontSize: '12px', color: 'var(--bw-muted)', fontWeight: 400 }}>(when open)</span>
            </Link>
          </div>
          <p style={{ fontSize: '17px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
            If this resonates, we'd love to hear from you.<br />
            If not, that clarity is a win too.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
